import { LineColors, LineOrientations, LineStates } from './types';
import { ICheckerView, IGameLine } from './interfaces';
import getPositionsForCheckers from './checkerPositions';
import ImageProvider from '@base/features/image/imageProvider';
import { BackgammonImageCollectionKeys } from '@backgammon/constants/imageCollection';
import Sizes from './sizes';
import Drawable from '@base/features/game/model/drawable';

export default class GameLine extends Drawable implements IGameLine {
  color?: LineColors;
  state = LineStates.STATE_NORMAL;
  orientation?: LineOrientations;
  text?: string;
  number?: number;
  checkers: ICheckerView[] = [];
  needLayout = true;

  constructor(
    private imageProvider: ImageProvider<BackgammonImageCollectionKeys>,
    private gameSizes: Sizes,
    context: CanvasRenderingContext2D
  ) {
    super(context);
  }

  addChecker(checker: ICheckerView) {
    this.checkers.push(checker);
    this.needLayout = true;
  }

  getTopChecker() {
    if (this.checkers.length === 0) {
      return null;
    }
    const checker = this.checkers[this.checkers.length - 1];
    this.checkers.splice(this.checkers.length - 1, 1);
    this.needLayout = true;
    return checker;
  }

  clear() {
    while (this.checkers.length) {
      this.checkers.pop();
    }
  }

  getPointForNewChecker() {
    const sign = this.orientation === LineOrientations.ORIENTATION_NORMAL ? -1 : 1;
    if (this.checkers.length === 0) {
      return {
        x: this.position.x,
        y: this.position.y + sign * (this.size.height / 2 - this.gameSizes.getCheckerSize().width / 2),
      };
    }
    const positions = getPositionsForCheckers(this, this.gameSizes, true);

    return positions[positions.length - 1];
  }

  layout() {
    this.updateCheckers();
    this.needLayout = false;
  }

  updateCheckers() {
    const positions = getPositionsForCheckers(this, this.gameSizes);
    for (let i = 0; i < this.checkers.length; i++) {
      this.checkers[i].position = positions[i];
      this.checkers[i].size = this.gameSizes.getCheckerSize();
    }
  }

  setColor(color: LineColors) {
    this.color = color;
    this.updateImage();
  }

  setState(state: LineStates) {
    this.state = state;
    this.updateImage();
  }

  getCheckersColor() {
    if (this.checkers.length > 0) {
      return this.checkers[0].getColor();
    }
    return null;
  }

  getCheckersCount() {
    return this.checkers.length;
  }

  setNumber(num: number, context: CanvasRenderingContext2D) {
    this.number = num;
    this.updateText(context);
  }

  getNumber() {
    if (this.number !== undefined) return this.number;
    throw new Error('No line number');
  }

  setOrientation(orientation: LineOrientations) {
    this.orientation = orientation;
    this.updateImage();
  }

  updateText(context: CanvasRenderingContext2D) {
    if (this.number) {
      context.fillText(this.number.toString(), this.position.x, this.position.y);
    }
  }

  render(context: CanvasRenderingContext2D) {
    super.render(context);
  }

  renderNumber(context: CanvasRenderingContext2D) {
    const numberPosition: any = {};
    const sign = this.orientation === LineOrientations.ORIENTATION_NORMAL ? -1 : 1;
    numberPosition.x = this.position.x;
    numberPosition.y = this.position.y + (sign * this.size.height) / 1.82;
    const fontSize = 12;
    context.textBaseline = 'middle';
    context.font = Math.round(fontSize * this.gameSizes.ratio) + 'px Arial';
    context.fillStyle = 'white';
    const text = this.number?.toString() || '';
    context.fillText(text, numberPosition.x - fontSize / 2, numberPosition.y);
  }

  renderCheckers(context: CanvasRenderingContext2D) {
    for (let i = 0; i < this.checkers.length; i++) {
      this.checkers[i].render(context);
    }
  }

  barImageSizeUpdate() {
    // Дроп зона оличается по размерам от других позиций
    const sign = this.orientation === LineOrientations.ORIENTATION_NORMAL ? 1 : -1;
    this.position.y += 10 * sign;
    this.size.width -= 15;
    this.size.height += 20;
  }

  updateImage() {
    if (this.state === LineStates.STATE_NORMAL) {
      if (this.color === LineColors.COLOR_DARK_GREEN) {
        this.image = this.imageProvider.get('LineDarkGreen');
      } else if (this.color === LineColors.COLOR_LIGHT_GREEN) {
        this.image = this.imageProvider.get('LineLightGreen');
      } else if (
        this.getNumber() === 0 ||
        this.getNumber() === 25 ||
        this.getNumber() === 26 ||
        this.getNumber() === 27
      ) {
        // TODO это линии без картинки (те котороые за доской)
        this.image = new Image();
      }
    } else if (this.state === LineStates.STATE_AVAILABLE) {
      if (
        this.getNumber() === 0 ||
        this.getNumber() === 25 ||
        this.getNumber() === 26 ||
        this.getNumber() === 27
      ) {
        this.image = this.imageProvider.get('BarImage');
        this.barImageSizeUpdate();
      } else {
        this.image = this.imageProvider.get('LineMove');
      }
    } else if (this.state === LineStates.STATE_UNDO) {
      if (this.getNumber() === 26 || this.getNumber() === 27) {
        this.image = this.imageProvider.get('BarImage');
        this.barImageSizeUpdate();
      } else {
        this.image = this.imageProvider.get('LineUndo');
      }
    }

    if (this.orientation === LineOrientations.ORIENTATION_NORMAL) {
      this.rotation = 0;
    } else {
      this.rotation = 180;
    }
  }
}
